import { ClipboardCheck, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

export function AssessmentsPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="risk-assessments"
        icon={ClipboardCheck}
        title="Risk Assessment Workflows Coming Soon"
        description="Comprehensive risk assessment workflows with findings tracking, approval processes, and automated risk scoring will be available soon."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Risk Assessments</h1>
          <p className="text-neutral-400 mt-1">
            Conduct and track formal risk assessments
          </p>
        </div>
        <button className="btn-primary">
          New Assessment
        </button>
      </div>

      {/* Metrics Strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Total Assessments</p>
              <p className="text-2xl font-bold text-white mt-1">64</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <ClipboardCheck className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">In Progress</p>
              <p className="text-2xl font-bold text-warning-500 mt-1">12</p>
            </div>
            <div className="p-3 bg-warning-500/20 rounded-lg">
              <Clock className="text-warning-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Completed</p>
              <p className="text-2xl font-bold text-success-500 mt-1">48</p>
            </div>
            <div className="p-3 bg-success-500/20 rounded-lg">
              <CheckCircle className="text-success-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Findings</p>
              <p className="text-2xl font-bold text-danger-500 mt-1">142</p>
            </div>
            <div className="p-3 bg-danger-500/20 rounded-lg">
              <AlertTriangle className="text-danger-500" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Assessments Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Assessments</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Title</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Progress</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Findings</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">ASS-001</td>
                <td className="py-3 px-4 text-sm text-white">Q4 2024 Cyber Risk Assessment</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Cybersecurity</td>
                <td className="py-3 px-4">
                  <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                    <div className="h-full bg-warning-500" style={{width: '65%'}}></div>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-warning-400">12</td>
                <td className="py-3 px-4"><span className="badge-warning">In Progress</span></td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">ASS-002</td>
                <td className="py-3 px-4 text-sm text-white">Third-Party Supplier Assessment</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Vendor Risk</td>
                <td className="py-3 px-4">
                  <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                    <div className="h-full bg-success-500" style={{width: '100%'}}></div>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-danger-400">18</td>
                <td className="py-3 px-4"><span className="badge-success">Completed</span></td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm font-mono text-primary-400">ASS-003</td>
                <td className="py-3 px-4 text-sm text-white">DORA Compliance Gap Analysis</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Compliance</td>
                <td className="py-3 px-4">
                  <div className="h-2 w-24 bg-neutral-800 rounded-full overflow-hidden">
                    <div className="h-full bg-success-500" style={{width: '100%'}}></div>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-warning-400">8</td>
                <td className="py-3 px-4"><span className="badge-success">Completed</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
